import { PENDO_CONFIG } from 'constants/Constant'

let initializedVisitorId = null
let isPendoFeatureEnabled = false

function setPendoFeatureEnabled(enabled) {
  isPendoFeatureEnabled = enabled === true
}

function trackPendoEvent(eventName, properties = {}) {
  if (typeof window === 'undefined') {
    return
  }
  if (!isPendoFeatureEnabled) {
    return
  }
  if (eventName && typeof window.pendo?.track === 'function') {
    try {
      window.pendo.track(eventName, properties)
    } catch (error) {
      console.error('Error tracking Pendo event:', error)
    }
  }
}

/**
 * dedupeId is used to deduplicate the event if the same event is triggered multiple times within the same session
 */
function trackPendoEventOncePerSession(eventName, dedupeId, properties = {}) {
  if (!eventName) {
    return
  }
  const id = String(dedupeId ?? '').trim()
  if (!id) {
    trackPendoEvent(eventName, properties)
    return
  }
  try {
    const storageKey = `pendoOnce:${eventName}:${id}`
    if (sessionStorage.getItem(storageKey)) {
      return
    }
    sessionStorage.setItem(storageKey, '1')
  } catch (e) {
    // quota / private mode / disabled storage: still track
  }
  trackPendoEvent(eventName, properties)
}

async function sha256(message) {
  const msgBuffer = new TextEncoder().encode(String(message || ''))
  const hashBuffer = await crypto.subtle.digest('SHA-256', msgBuffer)
  const hashArray = Array.from(new Uint8Array(hashBuffer))
  return hashArray.map((b) => b.toString(16).padStart(2, '0')).join('')
}

function loadPendoScript(apiKey) {
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    return
  }
  if (apiKey) {
    const scriptSrc = `${PENDO_CONFIG.scriptBaseUrl}/${apiKey}/pendo.js`
    const existingScript = document.querySelector(`script[src="${scriptSrc}"]`)

    if (!window.pendo && !existingScript) {
      ;(function (p, e, n, d) {
        let o, v, w, x, y, z
        o = (p[d] = p[d] || {})
        o._q = o._q || []
        v = ['initialize', 'identify', 'updateOptions', 'pageLoad', 'track', 'trackAgent']
        for (w = 0, x = v.length; w < x; ++w)
          (function (m) {
            o[m] =
              o[m] ||
              function () {
                o._q[m === v[0] ? 'unshift' : 'push']([m].concat([].slice.call(arguments, 0)))
              }
          })(v[w])
        y = e.createElement(n)
        y.async = !0
        y.src = scriptSrc
        z = e.getElementsByTagName(n)[0]
        z.parentNode.insertBefore(y, z)
      })(window, document, 'script', 'pendo')
    }
  }
}

function getUserRole(claims = {}) {
  return claims.customRoleName || claims.role || 'unknown'
}

async function initializePendo(user) {
  if (typeof window === 'undefined') {
    return
  }
  if (!isPendoFeatureEnabled) {
    return
  }
  try {
    const apiKey = PENDO_CONFIG.apiKey
    if (apiKey && user?.uid && user?.claims) {
      const companyId = user.claims.companyID
      const tenantName =
        user.claims.companyName || user.claims.friendlyId || companyId || 'unknown'
      const role = getUserRole(user.claims)
      const hashedUid = await sha256(user.uid)

      if (initializedVisitorId !== hashedUid) {
        loadPendoScript(apiKey)

        const initializePayload = {
          visitor: {
            id: hashedUid,
            role,
          },
          account: {
            id: tenantName,
          },
        }

        window.pendo?.initialize?.(initializePayload)

        initializedVisitorId = hashedUid
      }
    }
  } catch (error) {
    console.error('Error initializing Pendo:', error)
  }
}

export {
  sha256,
  loadPendoScript,
  initializePendo,
  setPendoFeatureEnabled,
  trackPendoEvent,
  trackPendoEventOncePerSession,
}
